import {useState, useCallback, useMemo} from "react";
import {
	GridFilterModel,
	GridSortModel,
	GridInitialState,
	GridCallbackDetails
} from "@mui/x-data-grid-pro";
import {
	useListUsers,
	UsersControllerListParams,
	UsersControllerListSortBy,
	UserAttributeFilterDto,
	UserAttributeFilterDtoField,
	DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE
} from "@/app2/shared/data";

/** Grid `field` names accepted for GET /api/_internal/users `sortBy`. */
const ALLOWED_SORT_FIELDS = new Set<string>(Object.values(UsersControllerListSortBy));

/** Columns using MUI date-only filter operators (`getGridDateOperators(false)`). */
const MUI_DATE_ONLY_USER_COLUMNS = new Set<string>(["hireDate", "titleStartDate"]);

/** ISO instant at noon UTC for the picked calendar day (API `@db.Date` / no time semantics). */
function filterValueToDateOnlyApiIso(value: unknown): string | null {
	if (value == null) return null;
	const raw = typeof value === "string" ? value.trim() : value;
	if (raw === "") return null;
	const ymdFromString =
		typeof raw === "string" && /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : null;
	if (ymdFromString && /^\d{4}-\d{2}-\d{2}$/.test(ymdFromString)) {
		return `${ymdFromString}T12:00:00.000Z`;
	}
	const d = raw instanceof Date ? raw : new Date(String(raw));
	if (Number.isNaN(d.getTime())) return null;
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}T12:00:00.000Z`;
}

function userMuiDateOnlyToCondition(
	field: string,
	operatorRaw: string | undefined,
	value: unknown
): UserAttributeFilterDto | null {
	const op = operatorRaw ?? "";
	if (op === "isEmpty") {
		return {field: field as UserAttributeFilterDtoField, operator: "em"};
	}
	if (op === "isNotEmpty") {
		return {field: field as UserAttributeFilterDtoField, operator: "pr"};
	}
	const valueStr = filterValueToDateOnlyApiIso(value);
	if (!valueStr) {
		return null;
	}
	const mapOp: Record<string, UserAttributeFilterDto["operator"]> = {
		is: "eq",
		not: "ne",
		after: "gt",
		onOrAfter: "ge",
		before: "lt",
		onOrBefore: "le"
	};
	const apiOp = mapOp[op];
	if (!apiOp) {
		return null;
	}
	return {field: field as UserAttributeFilterDtoField, operator: apiOp, value: valueStr};
}

const FILTERABLE_FIELDS: readonly string[] = [
	"firstname",
	"lastname",
	"email",
	"jobTitle",
	"jobCategory",
	"division",
	"supervisorName",
	"streak",
	"overdue",
	"completedModules",
	"createdAt",
	"employeeCode",
	"customFieldValue1",
	"customFieldValue2",
	"customFieldValue3",
	"hireDate",
	"titleStartDate"
];

interface useUsersListGridProps {
	initialFilterSettings: UsersControllerListParams;
	onFilterModelChange?: (filterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => void;
}

export const useUsersListGrid = ({initialFilterSettings, onFilterModelChange}: useUsersListGridProps) => {
	const [filterModel, setFilterModel] = useState<UsersControllerListParams>(initialFilterSettings);

	const {data, isLoading, isFetched, refetch} = useListUsers(filterModel);

	const rows = data?.data ?? [];
	const rowCount = data?.total ?? 0;

	const onPageChange = useCallback((page: number) => {
		setFilterModel((prev) => ({
			...prev,
			page: page + 1
		}));
	}, []);

	const onPageSizeChange = useCallback((pageSize: number) => {
		setFilterModel((prev) => ({
			...prev,
			pageSize,
			page: 1
		}));
	}, []);

	const onFilterChange = useCallback((newFilterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => {
		setFilterModel((prev) => {
			type GridFilterItemLike = {
				columnField?: string;
				field?: string;
				value?: unknown;
				operatorValue?: string;
				operator?: string;
			};

			const filters: UserAttributeFilterDto[] = (newFilterModel.items ?? []).flatMap((item: GridFilterItemLike) => {
				const field = item.columnField ?? item.field;
				if (!field || !FILTERABLE_FIELDS.includes(field)) {
					return [];
				}

				if (MUI_DATE_ONLY_USER_COLUMNS.has(field)) {
					const c = userMuiDateOnlyToCondition(field, item.operatorValue ?? item.operator, item.value);
					return c ? [c] : [];
				}

				const operator = (item.operatorValue ?? item.operator ?? "co") as UserAttributeFilterDto["operator"];
				const hasValue =
					item.value !== undefined && item.value !== null && String(item.value).trim() !== "";
				if (!(operator === "pr" || hasValue)) {
					return [];
				}

				let value: string | undefined;
				if (operator === "pr") {
					value = undefined;
				} else if (item.value instanceof Date) {
					value = item.value.toISOString();
				} else {
					value = String(item.value ?? "");
				}
				return [{field: field as UserAttributeFilterDtoField, operator, value}];
			});

			onFilterModelChange && onFilterModelChange(newFilterModel, details);
				
			return {
				...prev,
				page: 1,
				filters: filters.length > 0 ? { operator: newFilterModel.linkOperator, conditions: filters } : undefined
			};
		});
	}, []);

	const onSortModelChange = useCallback((sortModel: GridSortModel) => {
		setFilterModel((prev) => {
			const field = sortModel[0]?.field;
			const rawSort = sortModel[0]?.sort;
			const sortDirection: "asc" | "desc" =
				rawSort === "asc" || rawSort === "desc" ? rawSort : "asc";
			const sortBy = (
				sortModel.length > 0 && field && ALLOWED_SORT_FIELDS.has(field)
					? field
					: "lastname"
			) as UsersControllerListSortBy;
			return {
				...prev,
				page: 1,
				sortBy,
				sortDirection: sortModel.length > 0 ? sortDirection : "asc"
			};
		});
	}, []);

	const sortModel = useMemo<GridSortModel>(() => {
		const field = filterModel.sortBy ?? "lastname";
		const sort =
			filterModel.sortDirection === "asc" || filterModel.sortDirection === "desc"
				? filterModel.sortDirection
				: "asc";
		return [{field: ALLOWED_SORT_FIELDS.has(field) ? field : "lastname", sort}];
	}, [filterModel.sortBy, filterModel.sortDirection]);

	const defaultInitialState: GridInitialState = {
		pagination: {
			pageSize: filterModel.pageSize ?? DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE,
			page: (filterModel.page ?? 1) - 1
		},
		sorting: {
			sortModel: [{field: "lastname", sort: "asc"}]
		},
		columns: {
			columnVisibilityModel: {
				createdAt: false,
				phone: false,
				email: false,
				jobTitle: false
			}
		}
	};

	return {
		rows,
		rowCount,
		isLoading,
		isFetched,
		refetch,
		page: (filterModel.page ?? 1) - 1,
		pageSize: filterModel.pageSize ?? DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE,
		sortModel,
		onFilterChange,
		onPageChange,
		onPageSizeChange,
		onSortModelChange,
		defaultInitialState
	};
};
